import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, Breed } from 'shared/@generated/models';
import { BaseModalRef } from 'shared/components/base-modal';
import { BreedPickerPanel } from 'shared/components/breed-picker-panel';
import { Button } from 'shared/components/button';
import { BreedInputValues } from 'shared/components/form/breed-input';
import { BreedSelectionField } from 'shared/components/form/breed-selection-field';
import { TextInputRef } from 'shared/components/form/text-input';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { tryFocus } from 'shared/library/try-focus';
import { useBreeds } from 'shared/state/breeds';

const EMPTY_BREEDS: Breed[] = [];

export type BreedMultiSelectProps = {
  value?: BreedInputValues | null;
  onChange?: (value: BreedInputValues) => void;
  onSelectBreed?: (breed: Breed) => void;
  onSelectQuery?: (value: string) => void;
  onClose?: () => void;
  dense?: boolean;
  allowQuery?: boolean;
  animal?: Animal;
  initialBreeds?: Breed[];
  placeholder?: string;
  triggerPlaceholder?: string;
  historyEnabled?: boolean;
  trigger?: (modal: BaseModalRef) => ReactNode;
  maxSelections?: number;
  testID?: string;
};

export function BreedMultiSelect({
  value,
  onChange,
  onSelectBreed,
  onSelectQuery,
  onClose,
  dense,
  allowQuery = true,
  animal,
  initialBreeds = EMPTY_BREEDS,
  placeholder,
  triggerPlaceholder,
  trigger,
  maxSelections,
  testID = 'breed-multi-select',
}: BreedMultiSelectProps) {
  const { t } = useTranslation('components');
  const { breeds: loadedBreeds, loading, error, fetchBreeds } = useBreeds();
  const [query, setQuery] = useState('');
  const inputRef = useRef<TextInputRef>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selected = value ?? [];
  const allBreeds = useMemo(() => {
    const byId = new Map<number, Breed>();

    for (const breed of [...initialBreeds, ...loadedBreeds]) {
      byId.set(breed.id, breed);
    }

    return Array.from(byId.values());
  }, [initialBreeds, loadedBreeds]);
  const breeds = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    return allBreeds
      .filter((breed) => !animal || breed.animal === animal)
      .filter((breed) => breed.name.toLocaleLowerCase().includes(normalizedQuery))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allBreeds, animal, query]);

  useEffect(
    () => () => {
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
    },
    [],
  );

  function openModal(modal: BaseModalRef) {
    setQuery('');
    modal.open();

    if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
    focusTimeoutRef.current = setTimeout(() => tryFocus(inputRef), 160);
  }

  function remove(value: string | number) {
    onChange?.(selected.filter((item) => item !== value));
  }

  function toggleBreed(breed: Breed) {
    if (selected.includes(breed.id)) {
      remove(breed.id);
      return;
    }

    if (maxSelections !== undefined && selected.length >= maxSelections) return;

    onChange?.([...selected, breed.id]);
    onSelectBreed?.(breed);
  }

  function selectQuery(query: string) {
    const trimmedQuery = query.trim();
    const alreadySelected = selected.some(
      (item) =>
        typeof item === 'string' && item.toLocaleLowerCase() === trimmedQuery.toLocaleLowerCase(),
    );

    if (
      !allowQuery ||
      !trimmedQuery ||
      alreadySelected ||
      (maxSelections !== undefined && selected.length >= maxSelections)
    ) {
      return;
    }

    onChange?.([...selected, trimmedQuery]);
    onSelectQuery?.(trimmedQuery);
  }

  return (
    <FullscreenModal
      headerTitle={t('form.breed_input.choose_breed')}
      centered={Platform.OS === 'web'}
      className={'md:h-[min(40rem,96dvh)]'}
      scrollable={false}
      contentTestID={`${testID}-modal`}
      onClose={() => {
        if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
        onClose?.();
      }}
      trigger={(modal) =>
        trigger ? (
          trigger({ ...modal, open: () => openModal(modal) })
        ) : (
          <BreedSelectionField
            values={selected}
            breeds={allBreeds}
            placeholder={
              triggerPlaceholder ?? placeholder ?? t('form.breed_input.multiple_placeholder')
            }
            accessibilityLabel={t('form.breed_input.choose_breed')}
            onOpen={() => openModal(modal)}
            onRemove={remove}
            multiple={true}
            dense={dense}
            testID={`${testID}-trigger`}
            itemTestID={(item) =>
              typeof item === 'number' ? `${testID}-selected-breed` : `${testID}-selected-query`
            }
          />
        )
      }
      footer={(modal) => (
        <Button
          text={t('search_modal.done')}
          onPress={modal.close}
          center={true}
          testID={`${testID}-done`}
        />
      )}
    >
      <BreedPickerPanel
        query={query}
        setQuery={setQuery}
        breeds={breeds}
        allBreeds={allBreeds}
        loading={loading}
        error={error && allBreeds.length === 0}
        onRetry={() => void fetchBreeds()}
        selected={selected}
        multiple={true}
        allowQuery={allowQuery}
        onToggle={toggleBreed}
        onRemove={remove}
        onSelectQuery={selectQuery}
        maxSelections={maxSelections}
        placeholder={placeholder}
        searchInputRef={inputRef}
        inputTestID={`${testID}-text`}
        optionTestID={(breed) => {
          const slug = (breed.slug.en || breed.name)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');

          return `${testID}-option-${slug}`;
        }}
      />
    </FullscreenModal>
  );
}
